import { MESApiService, type spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type {
	CobbleRow,
	CobblesPickers,
	CobblesPickerCrew,
	CobblesPickerProductSize,
	CobblesPickerProductType,
	CobblesSortType,
	CobblesSummary,
} from "../models/CobblesReport";

export interface GetCobblesParams {
	start: string;
	end: string;
	crewId?: string | null;
	productSizeId?: number | null;
	productTypeId?: number | null;
	fromRow?: number | null;
	rowsToFetch?: number | null;
	sortType?: CobblesSortType | null;
	signal?: AbortSignal;
}

export interface GetCobblesResult {
	rows: CobbleRow[];
	summary: CobblesSummary;
}

export const getCobblesData = async ({
	start,
	end,
	crewId = null,
	productSizeId = null,
	productTypeId = null,
	fromRow = 0,
	rowsToFetch = 100,
	sortType = null,
	signal,
}: GetCobblesParams): Promise<GetCobblesResult> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "start", value: start },
		{ name: "end", value: end },
		{ name: "crewId", value: crewId },
		{ name: "productSizeId", value: productSizeId },
		{ name: "productTypeId", value: productTypeId },
		{ name: "timeZoneCode", value: null },
		{ name: "fromRow", value: fromRow },
		{ name: "rowsToFetch", value: rowsToFetch },
		{ name: "sortType", value: sortType },
	];

	const resp = await apiService.callV2(
		"[MILL].[GetCobblesDataWeb]",
		parameters,
		signal,
	);

	if (resp.ok) {
		const rows = get(resp, "data.tables[0].rows", []) as CobbleRow[];
		const summaryRow = get(resp, "data.tables[1].rows[0]", null) as {
			Count?: number;
			Duration?: number;
		} | null;
		const summary: CobblesSummary = summaryRow
			? {
					count: summaryRow.Count ?? 0,
					durationSeconds: summaryRow.Duration ?? 0,
				}
			: { count: 0, durationSeconds: 0 };

		return { rows, summary };
	}
	throw new Error(resp.message || "Error fetching cobbles");
};

export interface GetCobblesPickersParams {
	start: string;
	end: string;
	signal?: AbortSignal;
}

export const getCobblesPickers = async ({
	start,
	end,
	signal,
}: GetCobblesPickersParams): Promise<CobblesPickers> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "start", value: start },
		{ name: "end", value: end },
		{ name: "timeZoneCode", value: null },
		{ name: "onlyActives", value: true },
	];

	const resp = await apiService.callV2(
		"[MILL].[GetCobblesPickersDataWeb]",
		parameters,
		signal,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching cobble pickers");
	}

	// The pickers SP returns 4 result sets in a fixed order:
	//   tables[0] = meta { Index, Name } — informational only
	//   tables[1] = crews (CREWS)
	//   tables[2] = product sizes (PRODUCTSIZES)
	//   tables[3] = product types (PRODUCTTYPES)
	const crews = get(resp, "data.tables[1].rows", []) as CobblesPickerCrew[];
	const productSizes = get(
		resp,
		"data.tables[2].rows",
		[],
	) as CobblesPickerProductSize[];
	const productTypes = get(
		resp,
		"data.tables[3].rows",
		[],
	) as CobblesPickerProductType[];

	return { crews, productSizes, productTypes };
};
